import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { Call, Device } from "@twilio/voice-sdk";
import apiService from "../services/api";
import { normalizePhone } from "../utils/phoneUtils";

export type CallStatus = "connecting" | "ringing" | "in-call" | "ended" | "error";

export interface ActiveCall {
  patientId: number | string;
  patientName: string;
  phone: string; // +E164
  meetingId?: number;
  status: CallStatus;
  startedAt: number | null;
  error?: string;
}

interface StartCallOptions {
  phone: string;
  patientId: number | string;
  patientName: string;
  // When set, the call is recorded on the "meeting-{id}" conference (feeds the
  // meeting summary transcript) instead of "patient-{id}" (ad-hoc summaries).
  meetingId?: number;
}

interface CallContextValue {
  activeCall: ActiveCall | null;
  isMuted: boolean;
  startWhatsAppCall: (options: StartCallOptions) => Promise<void>;
  hangUp: () => void;
  toggleMute: () => void;
  dismissCall: () => void;
  requestCallPermission: (
    patientId: number | string,
    phone: string
  ) => Promise<{ success?: boolean; already_approved?: boolean; already_sent?: boolean; error?: string }>;
}

const CallContext = createContext<CallContextValue | null>(null);

// Twilio errors that mean the device is holding a token it can no longer call
// with (20104 expired, 20101/20103 invalid). A device in this state fails every
// connect() for the rest of the page's life, so it has to be rebuilt rather than
// reused - which is what left therapists unable to call anyone until a reload.
const TOKEN_ERROR_CODES = [20101, 20103, 20104];

const isTokenError = (error: any): boolean =>
  TOKEN_ERROR_CODES.includes(error?.code) ||
  TOKEN_ERROR_CODES.includes(error?.originalError?.code) ||
  /AccessToken/.test(error?.name || "");

export const useCall = (): CallContextValue => {
  const ctx = useContext(CallContext);
  if (!ctx) {
    throw new Error("useCall must be used within a CallProvider");
  }
  return ctx;
};

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  // Set when the device's token died while a call was up: destroying it there
  // would drop the therapist out of the conference, so it is thrown away on the
  // next call instead.
  const deviceStaleRef = useRef(false);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  const discardDevice = useCallback(() => {
    const device = deviceRef.current;
    deviceRef.current = null;
    deviceStaleRef.current = false;
    if (device) {
      try {
        device.destroy();
      } catch (error) {
        console.warn("[Call] Could not destroy the voice device", error);
      }
    }
  }, []);

  const markDeviceStale = useCallback(() => {
    if (callRef.current) {
      deviceStaleRef.current = true;
      return;
    }
    discardDevice();
  }, [discardDevice]);

  const getDevice = useCallback(async (): Promise<Device> => {
    if (deviceRef.current && deviceStaleRef.current) {
      discardDevice();
    }
    if (deviceRef.current) {
      return deviceRef.current;
    }
    const authToken = localStorage.getItem("token") || "";
    const { token } = await apiService.getVoiceToken(authToken);
    const device = new Device(token, {
      // Prefer Opus - WhatsApp legs are VoIP end to end.
      codecPreferences: ["opus", "pcmu"] as any,
    });
    device.on("tokenWillExpire", async () => {
      try {
        const refreshed = await apiService.getVoiceToken(localStorage.getItem("token") || "");
        device.updateToken(refreshed.token);
      } catch (error) {
        // The token this device holds is about to lapse and the renewal failed,
        // so give up on the device rather than let it go on failing calls.
        console.error("[Call] Voice token refresh failed", error);
        markDeviceStale();
      }
    });
    device.on("error", (error: any) => {
      console.error("[Call] Device error", error);
      if (isTokenError(error)) {
        markDeviceStale();
      }
    });
    deviceRef.current = device;
    return device;
  }, [discardDevice, markDeviceStale]);

  const startWhatsAppCall = useCallback(async ({ phone, patientId, patientName, meetingId }: StartCallOptions) => {
    if (callRef.current) {
      console.warn("[Call] A call is already in progress");
      return;
    }
    // The backend honors this number when it matches one of the patient's known
    // numbers (patient / parent 1 / parent 2); otherwise the call is rejected.
    // When no phone is passed, the backend falls back to get_phone(patient).
    const e164 = phone ? "+" + normalizePhone(phone) : "";
    setIsMuted(false);
    setActiveCall({ patientId, patientName, phone: e164, meetingId, status: "connecting", startedAt: null });
    try {
      const device = await getDevice();
      const params: Record<string, string> = { patient_id: String(patientId) };
      if (meetingId) {
        params.meeting_id = String(meetingId);
      }
      if (e164) {
        params.phone = e164;
      }
      // The browser leg joins the conference immediately, so we can't tell here
      // whether the patient answered. On any disconnect, ask the backend to cancel
      // WhatsApp legs to this patient that are still ringing - otherwise hanging up
      // before the patient answers leaves their phone ringing.
      const cancelRingingLeg = () => {
        apiService
          .cancelWhatsAppCall({ token: localStorage.getItem("token") || "", patient_id: Number(patientId), phone: e164 || undefined })
          .catch(() => { /* best effort - errors already logged in apiService */ });
      };
      // A refresh can be missed entirely - the laptop sleeps, the socket drops -
      // and then the first sign the token is dead is connect() failing. Rebuild
      // the device once with a fresh token so the therapist never has to reload
      // the page to place a call.
      let call: Call;
      try {
        call = await device.connect({ params });
      } catch (error: any) {
        if (!isTokenError(error) && deviceRef.current === device) {
          throw error;
        }
        console.warn("[Call] Voice token expired - rebuilding the device and retrying", error);
        discardDevice();
        call = await (await getDevice()).connect({ params });
      }
      callRef.current = call;
      call.on("ringing", () => {
        setActiveCall(prev => (prev ? { ...prev, status: "ringing" } : prev));
      });
      call.on("accept", () => {
        setActiveCall(prev => (prev ? { ...prev, status: "in-call", startedAt: Date.now() } : prev));
      });
      call.on("disconnect", () => {
        callRef.current = null;
        setIsMuted(false);
        setActiveCall(prev => (prev ? { ...prev, status: "ended" } : prev));
        cancelRingingLeg();
      });
      call.on("cancel", () => {
        callRef.current = null;
        setActiveCall(prev => (prev ? { ...prev, status: "ended" } : prev));
        cancelRingingLeg();
      });
      call.on("error", (error: any) => {
        console.error("[Call] Call error", error);
        callRef.current = null;
        setActiveCall(prev =>
          prev ? { ...prev, status: "error", error: error?.message || "Call failed" } : prev
        );
      });
      call.on("mute", (muted: boolean) => setIsMuted(muted));
    } catch (error: any) {
      console.error("[Call] Failed to start WhatsApp call", error);
      callRef.current = null;
      const detail = error?.response?.data?.detail;
      setActiveCall(prev =>
        prev
          ? {
              ...prev,
              status: "error",
              error: detail || error?.message || "Could not start the call",
            }
          : prev
      );
    }
  }, [discardDevice, getDevice]);

  const hangUp = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
    } else {
      deviceRef.current?.disconnectAll();
      setActiveCall(prev => (prev ? { ...prev, status: "ended" } : prev));
    }
  }, []);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    call.mute(!call.isMuted());
    setIsMuted(call.isMuted());
  }, []);

  const dismissCall = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
      callRef.current = null;
    }
    setIsMuted(false);
    setActiveCall(null);
  }, []);

  const requestCallPermission = useCallback(async (patientId: number | string, phone: string) => {
    const authToken = localStorage.getItem("token") || "";
    // Pass the number the call will actually dial so the permission request goes
    // to the same phone (the backend falls back to get_phone(patient) without it).
    return apiService.requestWhatsAppCallPermission({
      token: authToken,
      patient_id: Number(patientId),
      phone: phone || undefined,
    });
  }, []);

  return (
    <CallContext.Provider
      value={{ activeCall, isMuted, startWhatsAppCall, hangUp, toggleMute, dismissCall, requestCallPermission }}
    >
      {children}
    </CallContext.Provider>
  );
};
